// apps/web/src/pages/admin/account.tsx — Quick-Task 260920-u3p.
//
// Admin self-service email-change page (German: "Konto"). Renders
// inside the AdminShell layout (mounted in main.tsx under
// RequireAuth). Mirrors the Phase 2 settings/restaurant.tsx pattern:
//   - read-only display of the current email (from Query.me)
//   - form with a single "new email" field (zod-validated inline)
//   - submit fires the REQUEST_ADMIN_EMAIL_CHANGE mutation
//   - inline-form errors for the same-email + EMAIL_TAKEN guards
//   - toasts for success + rate-limit + generic failure
//
// XSRF double-submit is enforced by lib/api.ts (gqlRequest sends the
// x-xsrf-token header on every mutation). The server-side
// assertXsrf in apps/api/src/graphql/resolvers/admin-account.ts is
// the FIRST inner-line so a CSRF mismatch is not masked by a role
// violation in logs (Phase 2 pattern).

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ClientError } from "graphql-request";
import { gqlRequest } from "../../lib/api";
import {
  ME_ACCOUNT_QUERY,
  REQUEST_ADMIN_EMAIL_CHANGE_MUTATION,
} from "./account-operations";

interface MeAccountResponse {
  me: { id: string; email: string; role: string };
}

interface RequestEmailChangeResponse {
  requestAdminEmailChange: {
    pendingChangeId: string;
    newEmail: string;
    expiresAt: string;
  };
}

// Client-side mirror of apps/api/src/graphql/resolvers/admin-account.ts
// (requestInputSchema). The regex is intentionally permissive — the
// server's zod .email().max(120) is the source of truth. A typo here
// would be caught by the server's INVALID_INPUT response; the client
// check exists for fast UX feedback (no network round-trip on a
// malformed address).
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isLikelyEmail(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed.length === 0 || trimmed.length > 120) return false;
  return EMAIL_RE.test(trimmed);
}

type FieldError = string | null;

export function AdminAccountPage(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const meQuery = useQuery<MeAccountResponse>({
    queryKey: ["admin", "account", "me"],
    queryFn: async () => gqlRequest<MeAccountResponse>(ME_ACCOUNT_QUERY),
  });

  const [newEmail, setNewEmail] = useState<string>("");
  const [fieldError, setFieldError] = useState<FieldError>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [toastKind, setToastKind] = useState<"success" | "error" | null>(null);

  // Clear toast after 4s. The success toast hangs around long enough
  // for the operator to read the message; the error toasts clear
  // faster so a retry is friction-free.
  useEffect(() => {
    if (!toast) return;
    const ttl = toastKind === "success" ? 4000 : 3000;
    const handle = setTimeout(() => {
      setToast(null);
      setToastKind(null);
    }, ttl);
    return () => clearTimeout(handle);
  }, [toast, toastKind]);

  const mutation = useMutation({
    mutationFn: async (vars: { newEmail: string }) =>
      gqlRequest<RequestEmailChangeResponse>(
        REQUEST_ADMIN_EMAIL_CHANGE_MUTATION,
        vars,
      ),
    onSuccess: (res) => {
      // Pitfall 9 mirror — invalidate so a subsequent navigate-back
      // reads the freshly-cached `me.email` (it has NOT changed
      // yet — only PendingEmailChange was created — but the form
      // clears + the toast confirms the request landed).
      void queryClient.invalidateQueries({ queryKey: ["admin", "account"] });
      setNewEmail("");
      setFieldError(null);
      setToast(
        t("settings.account.requestSent", {
          newEmail: res.requestAdminEmailChange.newEmail,
        }),
      );
      setToastKind("success");
    },
    onError: (err) => {
      // Map by extensions.code per plan spec. The web client's
      // MESSAGE_KEYS map (apps/web/i18next.ts) renders the locale-
      // aware string; we only need to pick WHICH field/toast to
      // surface.
      if (err instanceof ClientError) {
        const code = err.response?.errors?.[0]?.extensions?.code as
          string | undefined;
        if (code === "EMAIL_TAKEN") {
          setFieldError(t("settings.account.errorEmailTaken"));
          return;
        }
        if (code === "ADMIN_EMAIL_CHANGE_RATE_LIMITED") {
          setFieldError(null);
          setToast(t("settings.account.errorRateLimited"));
          setToastKind("error");
          return;
        }
        if (code === "INVALID_INPUT") {
          // Server-side same-email guard or zod parse failure —
          // surface as the same client-side message because the
          // server's check is the source of truth.
          setFieldError(t("settings.account.errorSameEmail"));
          return;
        }
        if (code === "CSRF_TOKEN_MISMATCH") {
          // Refresh the page so the XSRF cookie + header are
          // re-issued by the next successful auth probe. The user
          // effectively has to re-login.
          window.location.reload();
          return;
        }
      }
      setFieldError(null);
      setToast(t("settings.account.errorGeneric"));
      setToastKind("error");
    },
  });

  function onSubmit(e: React.FormEvent): void {
    e.preventDefault();
    setFieldError(null);
    // Client-side same-email guard. The server ALSO checks this so a
    // misbehaving client cannot issue a no-op that would consume a
    // rate-limit bucket — but the client-side check gives the
    // operator a fast, offline error.
    if (
      meQuery.data?.me.email &&
      newEmail.toLowerCase() === meQuery.data.me.email.toLowerCase()
    ) {
      setFieldError(t("settings.account.errorSameEmail"));
      return;
    }
    const parsed = newEmail.trim();
    if (!isLikelyEmail(parsed)) {
      setFieldError(t("settings.account.errorSameEmail"));
      return;
    }
    mutation.mutate({ newEmail: parsed });
  }

  const currentEmail = meQuery.data?.me.email ?? "";
  const submitDisabled = mutation.isPending || newEmail.trim().length === 0;

  return (
    <main className="min-h-screen bg-bg text-text font-body">
      <header className="sticky top-0 z-10 bg-bg border-b border-divider px-4 py-3 flex items-center gap-3">
        <Link
          to="/admin/einstellungen"
          className="text-text/70 hover:text-text"
          aria-label={t("settings.hub.backToHub")}
          data-testid="account-back"
        >
          ‹
        </Link>
        <h1
          className="font-heading text-2xl md:text-3xl"
          data-testid="account-title"
        >
          {t("settings.account.title")}
        </h1>
      </header>

      {meQuery.isLoading && (
        <p className="px-4 py-6 text-text/70" data-testid="account-loading">
          …
        </p>
      )}

      {meQuery.isError && (
        <p className="px-4 py-6 text-text/70" data-testid="account-error">
          {t("settings.account.errorGeneric")}
        </p>
      )}

      {meQuery.data && (
        <form
          className="px-4 py-6 w-full max-w-3xl space-y-4"
          onSubmit={onSubmit}
          data-testid="account-form"
        >
          <p
            className="text-sm text-text/70 mb-4"
            data-testid="account-description"
          >
            {t("settings.account.description")}
          </p>

          <Field label={t("settings.account.currentEmailLabel")}>
            <input
              type="email"
              value={currentEmail}
              readOnly
              className="field w-full bg-surface text-text/70 cursor-not-allowed"
              data-testid="account-current-email"
              aria-readonly="true"
            />
          </Field>

          <Field label={t("settings.account.newEmailLabel")}>
            <input
              type="email"
              value={newEmail}
              onChange={(e) => {
                setNewEmail(e.target.value);
                if (fieldError) setFieldError(null);
              }}
              placeholder={t("settings.account.newEmailPlaceholder")}
              className="field w-full"
              data-testid="account-new-email-input"
              required
              autoComplete="email"
              maxLength={120}
            />
          </Field>

          {fieldError && (
            <p
              className="text-sm text-red-700"
              data-testid="account-field-error"
              role="alert"
            >
              {fieldError}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-3 pt-2">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitDisabled}
              data-testid="account-submit"
            >
              {t("settings.account.submit")}
            </button>
            <Link
              to="/admin/einstellungen"
              className="btn btn-secondary"
              data-testid="account-cancel"
            >
              {t("settings.restaurant.cancel")}
            </Link>
            {toast && (
              <span
                className={`text-sm ${toastKind === "error" ? "text-red-700" : "text-text/70"}`}
                data-testid="account-toast"
                role="status"
              >
                {toast}
              </span>
            )}
          </div>
        </form>
      )}
    </main>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-text/70 mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}
